import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "flowbite-react";
import ClientList from "./ClientList";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import ConfirmDialog from "./ConfirmDialog";
import PrintJobsheet from "./PrintJobsheet";
import { useSelector } from "react-redux";

const API = "https://eps.elkomprintama.com/";

const inp =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 transition";
const inpDisabled =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-400 cursor-not-allowed";
const lbl =
  "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

const JS_TABS = ["Header", "Material", "Process", "Other", "Print"];

/* ── Set Product Code Modal ───────────────────────────────────────── */
function SetProductModal({ job, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}get/api/store/mats`)
      .then((r) => setProducts(r.data?.result || []))
      .catch(() => toast.error("Failed to fetch products."));
    searchRef.current?.focus();
  }, []);

  const filtered = products.filter(
    (p) =>
      p.product_code?.toLowerCase().includes(search.toLowerCase()) ||
      p.product_name?.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelect = async (product) => {
    setIsLoading(true);
    try {
      await axios.patch(`${API}patch/api/jobsheet/${job.job_id}/product`, {
        product_id: product.product_id,
      });
      toast.success(`Product [${product.product_code}] assigned!`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to assign product.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-xl shadow-xl w-[520px] p-6">
        <h3 className="text-sm font-bold text-gray-700 mb-1">
          Set Product Code
        </h3>
        <p className="text-xs text-gray-500 mb-4">
          Job: <span className="font-semibold">{job.job_name}</span> (ID:{" "}
          {job.job_id})
        </p>
        <input
          ref={searchRef}
          type="text"
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg py-2 px-3 text-sm mb-3 focus:outline-none focus:border-blue-400"
          placeholder="Search product code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onClose();
          }}
        />
        <div className="overflow-y-auto max-h-72 border rounded-lg">
          <table className="text-xs w-full border-collapse">
            <thead className="bg-gray-700 text-white uppercase sticky top-0">
              <tr>
                <th className="px-3 py-2 border-r text-left">Code</th>
                <th className="px-3 py-2 border-r text-left">Name</th>
                <th className="px-3 py-2 text-left">Group</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="text-center py-4 text-gray-400 italic"
                  >
                    No products found
                  </td>
                </tr>
              ) : (
                filtered.map((p, i) => (
                  <tr
                    key={p.product_id}
                    className={`cursor-pointer hover:bg-blue-50 border-b ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                    onClick={() => !isLoading && handleSelect(p)}
                  >
                    <td className="px-3 py-2 border-r font-mono font-semibold text-blue-700">
                      {p.product_code}
                    </td>
                    <td className="px-3 py-2 border-r">{p.product_name}</td>
                    <td className="px-3 py-2 text-gray-500">{p.group_name}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <button
          className="mt-3 w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium py-2 rounded-lg"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

const radio = "w-4 h-4 accent-indigo-600 cursor-pointer";
const radioLbl =
  "flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer select-none";

/* ── Shared form fields ───────────────────────────────────────────── */
function JobsheetFormFields({
  activeTab,
  values,
  setters,
  materials,
  isEdit,
  job,
}) {
  const {
    jobDate,
    jobName,
    matId,
    matClr,
    size,
    ptg,
    pnCust,
    mpnElk,
    drwNo,
    dieNo,
    roll,
    core,
    flw,
    sht,
    cmpLabel,
    stdPack,
    pefo,
    required,
    lami,
    note,
    rollMode,
    hasLaminate,
    matSizes,
    lamiSizes,
  } = values;
  const {
    setJobDate,
    setJobName,
    setMatId,
    setMatClr,
    setSize,
    setPtg,
    setPnCust,
    setMpnElk,
    setDrwNo,
    setDieNo,
    setRoll,
    setCore,
    setFlw,
    setSht,
    setCmpLabel,
    setStdPack,
    setPefo,
    setRequired,
    setLami,
    setNote,
    setRollMode,
    setHasLaminate,
  } = setters;

  const isRoll = rollMode === "roll";

  const handleRollMode = (mode) => {
    setRollMode(mode);
    if (mode === "roll") {
      setSht("-");
      setCmpLabel("-");
      setFlw(flw === "Face Out" ? "Face Out" : "Face In");
    } else {
      setRoll("-");
      setCore("-");
      setFlw("");
    }
  };

  return (
    <div className="px-6 py-5">
      {/* Tab 1: Header */}
      {activeTab === 0 && (
        <div className="space-y-4">
          {isEdit && (
            <div>
              <label className={lbl}>
                Customer{" "}
                <span className="ml-1 text-gray-400 normal-case font-normal">
                  (locked)
                </span>
              </label>
              <input
                className={inpDisabled}
                value={job?.nme || ""}
                disabled
                readOnly
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {isEdit && (
              <div>
                <label className={lbl}>Job ID</label>
                <input
                  className={inpDisabled}
                  value={job?.job_id || ""}
                  disabled
                  readOnly
                />
              </div>
            )}
            <div>
              <label className={lbl}>Date</label>
              <DatePicker
                className={inp}
                wrapperClassName="w-full"
                selected={jobDate}
                onChange={setJobDate}
                dateFormat="dd/MM/yyyy"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>
                Description <span className="text-red-400">*</span>
              </label>
              <input
                className={inp}
                name="jobname"
                type="text"
                placeholder="Job description"
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Cust. P/N</label>
              <input
                className={inp}
                name="pncust"
                type="text"
                placeholder="Part number"
                value={pnCust}
                onChange={(e) => setPnCust(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>MPN (Elkom)</label>
              <input
                className={inp}
                name="mpnelk"
                type="text"
                placeholder="MPN"
                value={mpnElk}
                onChange={(e) => setMpnElk(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Material */}
      {activeTab === 1 && (
        <div className="space-y-4">
          {/* Material + Laminate checkbox */}
          <div>
            <label className={lbl}>
              Material {!isEdit && <span className="text-red-400">*</span>}
            </label>
            <div className="flex items-center gap-3">
              {isEdit ? (
                <input
                  className={`${inpDisabled} flex-1`}
                  value={job?.mat_name || ""}
                  disabled
                  readOnly
                />
              ) : (
                <select
                  className={`${inp} flex-1`}
                  value={matId}
                  onChange={(e) => {
                    setMatId(e.target.value);
                    setRequired("");
                  }}
                >
                  <option value="">— Select material —</option>
                  {materials.map((m) => (
                    <option key={m.mat_id} value={m.mat_id}>
                      {m.mat_name}
                    </option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-1.5 cursor-pointer whitespace-nowrap text-sm font-semibold text-indigo-700 select-none">
                <input
                  type="checkbox"
                  checked={hasLaminate}
                  onChange={(e) => {
                    setHasLaminate(e.target.checked);
                    if (!e.target.checked) setLami("");
                  }}
                  className="w-4 h-4 rounded accent-indigo-600"
                />
                + LAMINATE
              </label>
            </div>
          </div>

          {/* Required — fetched from mat_size based on material */}
          <div>
            <label className={lbl}>Material Requirement</label>
            {matSizes.length > 0 ? (
              <select
                className={inp}
                value={required}
                onChange={(e) => setRequired(e.target.value)}
              >
                <option value="">— Select requirement —</option>
                {matSizes.map((s, i) => (
                  <option key={i} value={s.mat_size}>
                    {s.mat_size}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className={inpDisabled}
                value={required}
                readOnly
                disabled
                placeholder={
                  isEdit
                    ? "No sizes found for this material"
                    : "Select a material first"
                }
              />
            )}
          </div>

          {/* Lamination dropdown — shown when checkbox is checked */}
          {hasLaminate && (
            <div>
              <label className={lbl}>Lamination</label>
              <select
                className={inp}
                value={lami}
                onChange={(e) => setLami(e.target.value)}
              >
                <option value="">— Select lamination —</option>
                {lamiSizes.map((s, i) => (
                  <option key={i} value={s.mat_size}>
                    {s.mat_size}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Material Colour</label>
              <input
                className={inp}
                name="matclr"
                type="text"
                placeholder="Colour"
                value={matClr}
                onChange={(e) => setMatClr(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Size</label>
              <input
                className={inp}
                name="size"
                type="text"
                placeholder="Size"
                value={size}
                onChange={(e) => setSize(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Printing</label>
              <input
                className={inp}
                name="ptg"
                type="text"
                placeholder="Printing"
                value={ptg}
                onChange={(e) => setPtg(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Process */}
      {activeTab === 2 && (
        <div className="space-y-5">
          {/* Roll / Sheet toggle */}
          <div>
            <label className={lbl}>Type</label>
            <div className="flex mt-1 rounded-lg border border-gray-300 w-fit overflow-hidden">
              <button
                type="button"
                onClick={() => handleRollMode("roll")}
                className={`px-8 py-2 text-sm font-bold transition ${isRoll ? "bg-indigo-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
              >
                Roll
              </button>
              <button
                type="button"
                onClick={() => handleRollMode("sheet")}
                className={`px-8 py-2 text-sm font-bold transition border-l border-gray-300 ${!isRoll ? "bg-indigo-600 text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
              >
                Sheet
              </button>
            </div>
          </div>

          {/* Roll card */}
          <div
            className={`rounded-xl border-2 p-4 transition-all ${isRoll ? "border-indigo-300 bg-indigo-50" : "border-dashed border-gray-200 bg-gray-50 opacity-50 pointer-events-none"}`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
              Roll
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Roll</label>
                <input
                  className={inp}
                  name="roll"
                  type="text"
                  placeholder="Roll"
                  value={isRoll ? roll : "-"}
                  onChange={(e) => setRoll(e.target.value)}
                />
              </div>
              <div>
                <label className={lbl}>Core</label>
                <input
                  className={inp}
                  name="core"
                  type="text"
                  placeholder="Core"
                  value={isRoll ? core : "-"}
                  onChange={(e) => setCore(e.target.value)}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className={lbl}>Flow Direction</label>
              <div className="flex gap-2 mt-1">
                {["Face In", "Face Out"].map((dir) => (
                  <button
                    key={dir}
                    type="button"
                    onClick={() => setFlw(dir)}
                    className={`px-5 py-1.5 rounded-lg border text-sm font-semibold transition ${flw === dir ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-300 hover:border-indigo-300"}`}
                  >
                    {dir}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sheet card */}
          <div
            className={`rounded-xl border-2 p-4 transition-all ${!isRoll ? "border-indigo-300 bg-indigo-50" : "border-dashed border-gray-200 bg-gray-50 opacity-50 pointer-events-none"}`}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-600 mb-3">
              Sheet
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={lbl}>Sheet</label>
                <input
                  className={inp}
                  name="sht"
                  type="text"
                  placeholder="Sheet"
                  value={!isRoll ? sht : "-"}
                  onChange={(e) => setSht(e.target.value)}
                />
              </div>
              <div>
                <label className={lbl}>Computer Label</label>
                <input
                  className={inp}
                  name="cmplabel"
                  type="text"
                  placeholder="Comp. label"
                  value={!isRoll ? cmpLabel : "-"}
                  onChange={(e) => setCmpLabel(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Drawing, Diecut & common fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl}>Drawing No.</label>
              <input
                className={inp}
                name="drwno"
                type="text"
                placeholder="Drawing"
                value={drwNo}
                onChange={(e) => setDrwNo(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Diecut No.</label>
              <input
                className={inp}
                name="dieno"
                type="text"
                placeholder="Diecut"
                value={dieNo}
                onChange={(e) => setDieNo(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Packing</label>
              <input
                className={inp}
                name="stdpack"
                type="text"
                placeholder="Packing"
                value={stdPack}
                onChange={(e) => setStdPack(e.target.value)}
              />
            </div>
            <div>
              <label className={lbl}>Perforation</label>
              <div className="flex gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setPefo("PEFORATED")}
                  className={`px-4 py-1.5 rounded-lg border text-xs font-bold transition ${pefo === "PEFORATED" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-300 hover:border-indigo-300"}`}
                >
                  PEFORATED
                </button>
                <button
                  type="button"
                  onClick={() => setPefo("-")}
                  className={`px-4 py-1.5 rounded-lg border text-xs font-bold transition ${pefo === "-" ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-600 border-gray-300 hover:border-indigo-300"}`}
                >
                  NO PEFORATED
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Other */}
      {activeTab === 3 && (
        <div className="space-y-4">
          {isEdit && (
            <div>
              <label className={lbl}>
                Product Code{" "}
                <span className="ml-1 text-gray-400 normal-case font-normal">
                  (use Set button)
                </span>
              </label>
              <input
                className={inpDisabled}
                value={job?.product_code || "not set"}
                disabled
                readOnly
              />
            </div>
          )}
          <div>
            <label className={lbl}>Note</label>
            <textarea
              className={`${inp} resize-none`}
              rows={5}
              placeholder="Notes..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Shared tab footer ────────────────────────────────────────────── */
function TabFooter({
  activeTab,
  setActiveTab,
  submitLabel,
  onCancel,
  color = "indigo",
  totalTabs,
}) {
  const total = totalTabs ?? JS_TABS.length;
  const isPrintTab = activeTab === total - 1 && submitLabel === "Save Changes";
  const bg =
    color === "indigo"
      ? "bg-indigo-600 hover:bg-indigo-700"
      : "bg-green-600 hover:bg-green-700";
  return (
    <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 bg-gray-50 rounded-b-2xl flex items-center justify-between">
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setActiveTab((t) => Math.max(0, t - 1))}
          disabled={activeTab === 0}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-30 transition"
        >
          ← Prev
        </button>
        <button
          type="button"
          onClick={() => setActiveTab((t) => Math.min(total - 1, t + 1))}
          disabled={activeTab === total - 1}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 disabled:opacity-30 transition"
        >
          Next →
        </button>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-100 transition"
        >
          Cancel
        </button>
        {!isPrintTab && (
          <button
            type="submit"
            className={`px-6 py-2 rounded-lg text-sm font-bold text-white ${bg} transition`}
          >
            {submitLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Add Jobsheet Modal ───────────────────────────────────────────── */
function AddJobsheetModal({ onClose, onSaved, materials }) {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [custId, setCustId] = useState(null);

  const [jobDate, setJobDate] = useState(new Date());
  const [jobName, setJobName] = useState("");
  const [matId, setMatId] = useState("");
  const [matClr, setMatClr] = useState("");
  const [size, setSize] = useState("");
  const [ptg, setPtg] = useState("");
  const [pnCust, setPnCust] = useState("");
  const [mpnElk, setMpnElk] = useState("");
  const [drwNo, setDrwNo] = useState("");
  const [dieNo, setDieNo] = useState("");
  const [roll, setRoll] = useState("");
  const [core, setCore] = useState("");
  const [flw, setFlw] = useState("Face In");
  const [sht, setSht] = useState("");
  const [cmpLabel, setCmpLabel] = useState("");
  const [stdPack, setStdPack] = useState("");
  const [pefo, setPefo] = useState("");
  const [required, setRequired] = useState("");
  const [lami, setLami] = useState("");
  const [note, setNote] = useState("");

  // Roll/Sheet & laminate UI state
  const [rollMode, setRollMode] = useState("roll");
  const [hasLaminate, setHasLaminate] = useState(false);
  const [matSizes, setMatSizes] = useState([]);
  const [lamiSizes, setLamiSizes] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}get/api/mat-sizes/4`, { withCredentials: true })
      .then((r) => setLamiSizes(r.data?.result || []))
      .catch(() => setLamiSizes([]));
  }, []);

  useEffect(() => {
    if (!matId) {
      setMatSizes([]);
      setRequired("");
      return;
    }
    axios
      .get(`${API}get/api/mat-sizes/${matId}`, { withCredentials: true })
      .then((r) => setMatSizes(r.data?.result || []))
      .catch(() => setMatSizes([]));
  }, [matId]);

  const handleCreate = async () => {
    const submitFlw = rollMode === "sheet" ? "" : flw;
    const submitRoll = rollMode === "sheet" ? "-" : roll;
    const submitCore = rollMode === "sheet" ? "-" : core;
    const submitSht = rollMode === "roll" ? "-" : sht;
    const submitCmpLabel = rollMode === "roll" ? "-" : cmpLabel;
    try {
      await axios.post(`${API}post/api/jobsheet`, {
        cust_id: custId,
        job_date: format(jobDate, "yyyy-MM-dd"),
        job_name: jobName,
        mat_id: matId || null,
        mat_clr: matClr,
        size,
        ptg,
        pn_cust: pnCust,
        mpn_elk: mpnElk,
        drw_no: drwNo,
        die_no: dieNo,
        roll: submitRoll,
        core: submitCore,
        flw: submitFlw,
        sht: submitSht,
        cmp_label: submitCmpLabel,
        std_pack: stdPack,
        pefo,
        required,
        lami,
        note,
        admin: user?.username || "",
      });
      toast.success("Jobsheet created!");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to create jobsheet.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!jobName.trim()) {
      toast.error("Description is required.");
      return;
    }
    if (!custId) {
      toast.error("Please select a customer.");
      return;
    }
    setShowConfirm(true);
  };

  const values = {
    jobDate,
    jobName,
    matId,
    matClr,
    size,
    ptg,
    pnCust,
    mpnElk,
    drwNo,
    dieNo,
    roll,
    core,
    flw,
    sht,
    cmpLabel,
    stdPack,
    pefo,
    required,
    lami,
    note,
    rollMode,
    hasLaminate,
    matSizes,
    lamiSizes,
  };
  const setters = {
    setJobDate,
    setJobName,
    setMatId,
    setMatClr,
    setSize,
    setPtg,
    setPnCust,
    setMpnElk,
    setDrwNo,
    setDieNo,
    setRoll,
    setCore,
    setFlw,
    setSht,
    setCmpLabel,
    setStdPack,
    setPefo,
    setRequired,
    setLami,
    setNote,
    setRollMode,
    setHasLaminate,
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 bg-green-600 rounded-t-2xl flex-shrink-0">
          <div>
            <h2 className="text-white font-bold text-base">New Jobsheet</h2>
            <span className="text-green-200 text-xs">
              Fill all tabs then click Create
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-green-200 text-2xl font-bold leading-none"
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 flex-shrink-0">
            {JS_TABS.map((tab, i) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(i)}
                className={`px-5 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none ${activeTab === i ? "bg-green-600 text-white" : "text-gray-500 hover:text-green-600 hover:bg-green-50"}`}
              >
                {i + 1}. {tab}
              </button>
            ))}
          </div>

          <div className="overflow-y-auto flex-1">
            {/* Customer injected here for Add mode tab 1 */}
            {activeTab === 0 && (
              <div className="px-6 py-5 space-y-4">
                <div>
                  <label className={lbl}>
                    Customer <span className="text-red-400">*</span>
                  </label>
                  <ClientList
                    onChangeList={setCustId}
                    onInitial={setCustId}
                    showLabel={false}
                  />
                </div>
                <div>
                  <label className={lbl}>Date</label>
                  <DatePicker
                    className={inp}
                    wrapperClassName="w-full"
                    selected={jobDate}
                    onChange={setJobDate}
                    dateFormat="dd/MM/yyyy"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={lbl}>
                      Description <span className="text-red-400">*</span>
                    </label>
                    <input
                      className={inp}
                      name="jobname"
                      type="text"
                      placeholder="Job description"
                      value={jobName}
                      onChange={(e) => setJobName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={lbl}>Cust. P/N</label>
                    <input
                      className={inp}
                      name="pncust"
                      type="text"
                      placeholder="Part number"
                      value={pnCust}
                      onChange={(e) => setPnCust(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className={lbl}>MPN (Elkom)</label>
                    <input
                      className={inp}
                      name="mpnelk"
                      type="text"
                      placeholder="MPN"
                      value={mpnElk}
                      onChange={(e) => setMpnElk(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
            {activeTab !== 0 && (
              <JobsheetFormFields
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                values={values}
                setters={setters}
                materials={materials}
                isEdit={false}
              />
            )}
          </div>

          <TabFooter
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            submitLabel="Create Jobsheet"
            onCancel={onClose}
            color="green"
          />
        </form>

        {showConfirm && (
          <ConfirmDialog
            message="Create this jobsheet?"
            onConfirm={() => {
              setShowConfirm(false);
              handleCreate();
            }}
            onCancel={() => setShowConfirm(false)}
          />
        )}
      </div>
    </div>
  );
}

/* ── Edit Jobsheet Modal ──────────────────────────────────────────── */
function EditJobsheetModal({ job, onClose, onSaved, materials }) {
  const [activeTab, setActiveTab] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [printJob, setPrintJob] = useState(null);

  const [jobDate, setJobDate] = useState(new Date());
  const [jobName, setJobName] = useState("");
  const [matClr, setMatClr] = useState("");
  const [size, setSize] = useState("");
  const [ptg, setPtg] = useState("");
  const [pnCust, setPnCust] = useState("");
  const [mpnElk, setMpnElk] = useState("");
  const [drwNo, setDrwNo] = useState("");
  const [dieNo, setDieNo] = useState("");
  const [roll, setRoll] = useState("");
  const [core, setCore] = useState("");
  const [flw, setFlw] = useState("Face In");
  const [sht, setSht] = useState("");
  const [cmpLabel, setCmpLabel] = useState("");
  const [stdPack, setStdPack] = useState("");
  const [pefo, setPefo] = useState("");
  const [required, setRequired] = useState("");
  const [lami, setLami] = useState("");
  const [note, setNote] = useState("");

  // Roll/Sheet & laminate UI state
  const [rollMode, setRollMode] = useState("roll");
  const [hasLaminate, setHasLaminate] = useState(false);
  const [matSizes, setMatSizes] = useState([]);
  const [lamiSizes, setLamiSizes] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}get/api/mat-sizes/4`, { withCredentials: true })
      .then((r) => setLamiSizes(r.data?.result || []))
      .catch(() => setLamiSizes([]));
  }, []);

  useEffect(() => {
    if (!job) return;
    setJobDate(job.job_date ? new Date(job.job_date) : new Date());
    setJobName(job.job_name || "");
    setMatClr(job.mat_clr || "");
    setSize(job.size || "");
    setPtg(job.ptg || "");
    setPnCust(job.pn_cust || "");
    setMpnElk(job.mpn_elk || "");
    setDrwNo(job.drw_no || "");
    setDieNo(job.die_no || "");
    setRoll(job.roll || "");
    setCore(job.core || "");
    setFlw(job.flw === "Face Out" ? "Face Out" : "Face In");
    setSht(job.sht || "");
    setCmpLabel(job.cmp_label || "");
    setStdPack(job.std_pack || "");
    setPefo(job.pefo || "");
    setRequired(job.required || "");
    setLami(job.lami || "");
    setNote(job.note || "");
    // Derive roll/sheet mode: if roll is "-" or empty while sht has value → sheet mode
    const isSheetMode =
      job.roll === "-" || (!job.roll && job.sht && job.sht !== "-");
    setRollMode(isSheetMode ? "sheet" : "roll");
    setHasLaminate(!!job.lami && job.lami !== "");
    // Fetch mat sizes for required dropdown
    if (job.mat_id) {
      axios
        .get(`${API}get/api/mat-sizes/${job.mat_id}`, { withCredentials: true })
        .then((r) => setMatSizes(r.data?.result || []))
        .catch(() => setMatSizes([]));
    }
  }, [job]);

  const handleSave = async () => {
    const isRoll = rollMode === "roll";
    try {
      await axios.patch(`${API}patch/api/jobsheet/${job.job_id}`, {
        job_date: format(jobDate, "yyyy-MM-dd"),
        job_name: jobName,
        mat_clr: matClr,
        size,
        ptg,
        pn_cust: pnCust,
        mpn_elk: mpnElk,
        drw_no: drwNo,
        die_no: dieNo,
        roll: isRoll ? roll : "-",
        core: isRoll ? core : "-",
        flw: isRoll ? flw : "",
        sht: isRoll ? "-" : sht,
        cmp_label: isRoll ? "-" : cmpLabel,
        std_pack: stdPack,
        pefo,
        required,
        lami,
        note,
      });
      toast.success("Jobsheet saved! Go to the Print tab to print.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update jobsheet.");
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!jobName.trim()) {
      toast.error("Description is required.");
      return;
    }
    setShowConfirm(true);
  };

  const values = {
    jobDate,
    jobName,
    matId: "",
    matClr,
    size,
    ptg,
    pnCust,
    mpnElk,
    drwNo,
    dieNo,
    roll,
    core,
    flw,
    sht,
    cmpLabel,
    stdPack,
    pefo,
    required,
    lami,
    note,
    rollMode,
    hasLaminate,
    matSizes,
    lamiSizes,
  };
  const setters = {
    setJobDate,
    setJobName,
    setMatId: () => {},
    setMatClr,
    setSize,
    setPtg,
    setPnCust,
    setMpnElk,
    setDrwNo,
    setDieNo,
    setRoll,
    setCore,
    setFlw,
    setSht,
    setCmpLabel,
    setStdPack,
    setPefo,
    setRequired,
    setLami,
    setNote,
    setRollMode,
    setHasLaminate,
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
          <div className="flex items-center justify-between px-6 py-4 bg-indigo-600 rounded-t-2xl flex-shrink-0">
            <div>
              <h2 className="text-white font-bold text-base">Edit Jobsheet</h2>
              <span className="text-indigo-200 text-xs">
                ID: {job.job_id} · {job.nme}
              </span>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-white hover:text-indigo-200 text-2xl font-bold leading-none"
            >
              ✕
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="flex border-b border-gray-200 px-4 pt-3 gap-1 flex-shrink-0">
              {JS_TABS.map((tab, i) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(i)}
                  className={`px-5 py-2 text-sm font-semibold rounded-t-lg transition focus:outline-none ${activeTab === i ? "bg-indigo-600 text-white" : "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50"}`}
                >
                  {i + 1}. {tab}
                </button>
              ))}
            </div>

            <div className="overflow-y-auto flex-1">
              {activeTab === 4 ? (
                <div className="px-6 py-10 flex flex-col items-center gap-4">
                  <div className="text-5xl">🖨️</div>
                  <div className="text-center">
                    <p className="text-base font-bold text-gray-800">
                      Print Jobsheet{" "}
                      <span className="text-indigo-600">#{job.job_id}</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Make sure you have saved any changes before printing.
                    </p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-xl px-6 py-4 text-xs text-gray-600 w-full max-w-md space-y-1">
                    <p>
                      <span className="font-semibold text-gray-500 w-32 inline-block">
                        Customer:
                      </span>
                      {job.nme}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-500 w-32 inline-block">
                        Description:
                      </span>
                      {jobName}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-500 w-32 inline-block">
                        Size:
                      </span>
                      {size}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-500 w-32 inline-block">
                        Material:
                      </span>
                      {job.mat_name}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-500 w-32 inline-block">
                        Product Code:
                      </span>
                      {job.product_code || "not set"}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-10 py-3 rounded-xl text-sm transition"
                    onClick={() => {
                      const merged = {
                        ...job,
                        job_date: jobDate,
                        job_name: jobName,
                        mat_clr: matClr,
                        size,
                        ptg,
                        pn_cust: pnCust,
                        mpn_elk: mpnElk,
                        drw_no: drwNo,
                        die_no: dieNo,
                        roll,
                        core,
                        flw,
                        sht,
                        cmp_label: cmpLabel,
                        std_pack: stdPack,
                        pefo,
                        required,
                        lami,
                        note,
                      };
                      setPrintJob(merged);
                    }}
                  >
                    🖨️ Print Now
                  </button>
                </div>
              ) : (
                <JobsheetFormFields
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  values={values}
                  setters={setters}
                  materials={materials}
                  isEdit={true}
                  job={job}
                />
              )}
            </div>

            <TabFooter
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              submitLabel="Save Changes"
              onCancel={onClose}
              color="indigo"
            />
          </form>

          {showConfirm && (
            <ConfirmDialog
              message="Save changes to this jobsheet?"
              onConfirm={() => {
                setShowConfirm(false);
                handleSave();
              }}
              onCancel={() => setShowConfirm(false)}
            />
          )}
        </div>
      </div>

      {printJob && (
        <PrintJobsheet
          job={printJob}
          onDone={() => {
            setPrintJob(null);
            onClose();
          }}
        />
      )}
    </>
  );
}

/* ── JobsheetList ─────────────────────────────────────────────────── */
const JobsheetList = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [rows, setRows] = useState(0);
  const [desc, setDesc] = useState("");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [setProductTarget, setSetProductTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [lastSearch, setLastSearch] = useState({ custId: null, search: "" });
  const [materials, setMaterials] = useState([]);

  // By Job ID tab state
  const [jobIdInput, setJobIdInput] = useState("");
  const [jobIdLoading, setJobIdLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}get/api/materials`, { withCredentials: true })
      .then((r) => setMaterials(r.data?.result || []))
      .catch(() => {});
  }, []);

  function applyStripedStyle(index) {
    return index % 2 === 0 ? "border-b bg-gray-50" : "bg-white";
  }

  const searchbyCustomer = (e) => {
    e.preventDefault();
    getJobsById(selectedClientId);
  };

  const getJobsById = async (cust_id) => {
    try {
      const response = await axios.get(
        `${API}get/api/jobsheetlist/${cust_id}?search=${desc}`,
      );
      setJobs(response.data.result);
      setRows(response.data.totalRows[0].totalRows);
      setLastSearch({ custId: cust_id, search: desc });
    } catch {
      toast.error("Failed to fetch jobsheets.");
    }
  };

  const searchByJobId = async (e) => {
    e.preventDefault();
    const id = jobIdInput.trim();
    if (!id) return;
    setJobIdLoading(true);
    try {
      const res = await axios.get(`${API}get/api/jobsheetlist/search/${id}`, {
        withCredentials: true,
      });
      setJobs(res.data.result);
      setRows(res.data.totalRows[0].totalRows);
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Jobsheet not found.");
      setJobs([]);
      setRows(0);
    } finally {
      setJobIdLoading(false);
    }
  };

  const refreshJobs = () => {
    if (activeTab === 0 && lastSearch.custId) getJobsById(lastSearch.custId);
    if (activeTab === 1 && jobIdInput.trim())
      searchByJobId({ preventDefault: () => {} });
  };

  return (
    <>
      <div className="flex flex-col h-full bg-gray-200 px-5 py-5 sm:rounded-lg overflow-hidden">
        {/* Tab bar */}
        <div className="flex gap-1 mb-4 flex-shrink-0">
          {["By Customer", "By Jobsheet ID"].map((tab, i) => (
            <button
              key={tab}
              type="button"
              onClick={() => {
                setActiveTab(i);
                setJobs([]);
                setRows(0);
              }}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition ${
                activeTab === i
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-500 border border-gray-300 hover:bg-gray-50"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* By Customer form */}
        {activeTab === 0 && (
          <form onSubmit={searchbyCustomer} className="mb-4 flex-shrink-0">
            <div className="flex flex-wrap -mx-3 mb-3">
              <div className="w-full md:w-1/2 px-3">
                <ClientList
                  onChangeList={(id) => {
                    setSelectedClientId(id);
                    setDesc("");
                  }}
                  onInitial={setSelectedClientId}
                />
              </div>
              <div className="w-full md:w-1/2 px-3">
                <label
                  htmlFor="search_panel"
                  className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2"
                >
                  Search
                </label>
                <input
                  className="appearance-none block w-full border bg-white border-green-400 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                  id="search_panel"
                  name="desc"
                  type="text"
                  placeholder="Search Description"
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                />
              </div>
            </div>
            <Button fullSized type="submit">
              Search Jobsheet
            </Button>
          </form>
        )}

        {/* By Job ID form */}
        {activeTab === 1 && (
          <form
            onSubmit={searchByJobId}
            className="mb-4 flex-shrink-0 flex gap-2 items-end"
          >
            <div className="flex-1">
              <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                Jobsheet ID
              </label>
              <input
                className="appearance-none block w-full border bg-white border-indigo-400 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                type="number"
                placeholder="e.g. 1023"
                value={jobIdInput}
                onChange={(e) => setJobIdInput(e.target.value)}
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={jobIdLoading}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold rounded-lg transition disabled:opacity-50"
            >
              {jobIdLoading ? "Searching…" : "Search"}
            </button>
          </form>
        )}

        <p className="text-gray-900 text-xs mb-2 flex-shrink-0">
          Total Rows: {rows}
        </p>
        <div className="flex-1 min-h-0 overflow-auto shadow-md sm:rounded-lg">
          <table className="text-sm text-left text-gray-500 border-collapse">
            <thead className="sticky top-0 text-xs text-white uppercase bg-gray-600">
              <tr>
                <th className="whitespace-nowrap px-4 py-3 border-r bg-blue-700">
                  Product Code
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r text-center">
                  Edit
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Job ID</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Input Date
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Customer
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Description
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Material
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Mat. Colour
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Size</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Printing
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">P/N</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">MPN</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Drawing
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Diecut</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Roll</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Core</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Flow</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Sheet</th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Comp. Label
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Packing
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Perforation
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Required
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">
                  Lamination
                </th>
                <th className="whitespace-nowrap px-4 py-3 border-r">Note</th>
                <th className="whitespace-nowrap px-4 py-3">Admin</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, index) => (
                <tr
                  key={job.job_id}
                  className={`text-xs border-b hover:bg-indigo-50 ${applyStripedStyle(index)}`}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 border-r bg-blue-50">
                    <div className="flex items-center gap-2">
                      {job.product_code ? (
                        <span className="font-mono text-blue-700 font-bold">
                          {job.product_code}
                        </span>
                      ) : (
                        <span className="text-gray-300 italic">not set</span>
                      )}
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-2 py-0.5 rounded"
                        onClick={() => setSetProductTarget(job)}
                      >
                        Set
                      </button>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r text-center">
                    <button
                      className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded"
                      onClick={() => setEditTarget(job)}
                    >
                      ✏️ Edit Jobsheet
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r text-gray-500">
                    {job.job_id}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {new Date(job.job_date).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r font-medium">
                    {job.nme}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.job_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.mat_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.mat_clr}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.size}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.ptg}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.pn_cust}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.mpn_elk}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.drw_no}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.die_no}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.roll}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.core}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.flw}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.sht}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.cmp_label}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.std_pack}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.pefo}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.required}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.lami}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 border-r">
                    {job.note}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">{job.admin}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {setProductTarget && (
        <SetProductModal
          job={setProductTarget}
          onClose={() => setSetProductTarget(null)}
          onSaved={refreshJobs}
        />
      )}
      {editTarget && (
        <EditJobsheetModal
          job={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={refreshJobs}
          materials={materials}
        />
      )}
    </>
  );
};

export default JobsheetList;
